import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { gql } from "@apollo/client";
import { LoadingSlide } from "@components/title-slide-view";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { TextInput } from "@mantine/core";
import {
  type FetchEmailExclusions,
  type UpdateEmailExclusions,
  type UpdateEmailExclusionsVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";

export function EmailExclusions() {
  const { data, loading } = useNorthbeamQuery<FetchEmailExclusions>(
    FETCH_EMAIL_EXCLUSIONS,
    {},
  );

  const [updateEmailExclusions, { loading: updatingEmails }] =
    useNorthbeamMutation<UpdateEmailExclusions, UpdateEmailExclusionsVariables>(
      UPDATE_EMAIL_EXCLUSIONS,
      {
        refetchQueries: [{ query: FETCH_EMAIL_EXCLUSIONS }],
        awaitRefetchQueries: true,
      },
    );

  const [emailExclusions, setEmailExclusions] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");

  const errorMsg = useMemo(() => {
    const exclusionsSet = new Set(emailExclusions);
    return exclusionsSet.has(newEmail) ? "Email already exists" : null;
  }, [emailExclusions, newEmail]);

  useEffect(() => {
    if (data) {
      setEmailExclusions(data.me.emailExclusions);
    }
  }, [data]);

  if (loading || !data) {
    return <LoadingSlide />;
  }

  return (
    <div>
      <h1>Email address exclusions</h1>
      <div>
        If you have email addresses that are used for testing your site, you
        should add them to this exclusion list.
      </div>
      <div className="mt-5 font-semibold">Examples of emails to exclude</div>
      <ul className="list-disc list-inside">
        <li>Your company employees emails</li>
        <li>Testing emails</li>
      </ul>
      <div className="mt-5 font-semibold text-lg">Emails to exclude</div>
      {!emailExclusions.length ? (
        <div>No emails excluded</div>
      ) : (
        <ul className="list-disc list-inside">
          {emailExclusions.map((email, i) => {
            return (
              <li key={i}>
                <span>
                  {email}{" "}
                  <i
                    aria-label="Delete email"
                    className="fa-regular fa-sharp fa-times"
                    style={{ color: "red", cursor: "pointer" }}
                    onClick={() => {
                      const emailsToUpdateAs = emailExclusions.filter(
                        (e) => e !== email,
                      );
                      updateEmailExclusions({
                        variables: {
                          emails: emailsToUpdateAs,
                        },
                      });
                      setEmailExclusions(emailsToUpdateAs);
                    }}
                  />
                </span>
                <span></span>
              </li>
            );
          })}
        </ul>
      )}
      <div>
        <div className=" gap-5 inline-flex mt-2">
          <TextInput
            label="Add email"
            className="w-[25rem]"
            value={newEmail}
            placeholder="Email"
            onChange={({ target }) =>
              setNewEmail(target.value.toLocaleLowerCase().trim())
            }
            error={errorMsg}
          />
          <PrimaryButton
            className="h-8 mt-8 w-24"
            disabled={!newEmail || !!errorMsg}
            loading={updatingEmails}
            onClick={() => {
              updateEmailExclusions({
                variables: { emails: [...emailExclusions, newEmail] },
              });
              setNewEmail("");
            }}
          >
            {updatingEmails ? "Please Wait..." : "ADD EMAIL"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export const FETCH_EMAIL_EXCLUSIONS = gql`
  query FetchEmailExclusions {
    me {
      emailExclusions
    }
  }
`;

export const UPDATE_EMAIL_EXCLUSIONS = gql`
  mutation UpdateEmailExclusions($emails: [String!]!) {
    updateEmailExclusions(emails: $emails)
  }
`;
